// Interactive HTML tag names that should not forward clicks
const INTERACTIVE_TAGS = new Set(['button', 'a', 'input', 'select', 'textarea']);
// Interactive ARIA roles that should not forward clicks
const INTERACTIVE_ROLES = new Set(['button', 'link', 'checkbox', 'radio', 'switch', 'menuitem']);
/**
 * Handles click events on the card root, forwarding clicks to the main button
 * unless the click originated from an interactive element.
 *
 * @param e - The mouse event from the card root
 * @param mainButtonRef - Reference to the main button/link element
 */
function handleCardClick(e, mainButtonRef) {
    if (!shouldForwardEvent(e, mainButtonRef))
        return;
    // No clickable element found in the event path, forward to main button
    mainButtonRef.current?.click();
}
/**
 * Handles context menu events on the card root, forwarding the event to the main button
 * unless the right-click originated from an interactive element.
 *
 * @param e - The mouse event from the card root
 * @param mainButtonRef - Reference to the main button/link element
 */
function handleCardContextMenu(e, mainButtonRef) {
    if (!shouldForwardEvent(e, mainButtonRef))
        return;
    // Re-target the event to the main button to trigger its context menu handler
    if (mainButtonRef.current) {
        e.preventDefault();
        e.stopPropagation();
        // Create a synthetic MouseEvent to dispatch to the main button
        // Note: We don't explicitly set 'view' because:
        // 1. When dispatched, the browser automatically sets it to the element's document's defaultView
        // 2. Both source and target elements are in the same document, so view context is preserved
        const syntheticEvent = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            button: e.button,
            buttons: e.buttons,
            clientX: e.clientX,
            clientY: e.clientY,
            screenX: e.screenX,
            screenY: e.screenY,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            altKey: e.altKey,
            metaKey: e.metaKey,
        });
        mainButtonRef.current.dispatchEvent(syntheticEvent);
    }
}
/**
 * Helper function to check if the event should be forwarded to the main button.
 * Returns true if the event should be forwarded, false otherwise.
 */
function shouldForwardEvent(e, mainButtonRef) {
    // Don't forward if clicking on the main button itself
    const clickedOnMainButton = e.target === mainButtonRef.current ||
        (mainButtonRef.current && mainButtonRef.current.contains(e.target));
    if (clickedOnMainButton)
        return false;
    // Check if the ORIGINAL click target (e.target) or any of its ancestors
    // up to the Root is inside a clickable element that should consume the click.
    //
    // Note: This list is related to but smaller than the autoInteractive CSS selector list.
    // The CSS list includes elements that need pointer-events (like [title] for tooltips),
    // but this list only includes elements that should NOT forward clicks to MainButton.
    // Elements like [title] and [data-tooltip-type] are intentionally excluded here so
    // clicks on tooltips still trigger the main action.
    let target = e.target;
    while (target && target !== e.currentTarget) {
        // Skip if element is a clickable element (has its own click behavior)
        const tagName = target.tagName.toLowerCase();
        const role = target.getAttribute('role');
        if (INTERACTIVE_TAGS.has(tagName) || (role && INTERACTIVE_ROLES.has(role))) {
            // Found a clickable element in the path, don't forward
            return false;
        }
        target = target.parentElement;
    }
    return true;
}

export { handleCardClick, handleCardContextMenu };
//# sourceMappingURL=card-primitive.utils.js.map
